import { Loader2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AuthApiError, useAuth } from "@/lib/auth";
import type { AdminUserRow, UserRole, UserStatus } from "@/lib/auth/admin-api";
import { updateUser } from "@/lib/auth/admin-api";

interface EditUserDialogProps {
  open: boolean;
  user: AdminUserRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: (updated: AdminUserRow) => void;
}

function editErrorMessage(err: unknown): string {
  if (err instanceof AuthApiError) {
    switch (err.code) {
      case "self_modification_blocked":
        return (
          err.serverMessage ??
          "You can't change your own role or status from this dialog."
        );
      case "last_admin":
        return "At least one active admin must remain. Promote another user to admin first.";
      case "not_found":
        return "That user no longer exists. Refresh the list and try again.";
      case "validation_failed":
        return "Please check the values and try again.";
      default:
        return err.serverMessage ?? "Couldn't update the user. Please try again.";
    }
  }
  return "Couldn't reach the server. Check your connection and retry.";
}

// Edit name / role / status. Role + status are hard-disabled when the
// target is the signed-in admin so a self-lockout isn't even attemptable
// from the UI (the backend also refuses; this just makes the rule visible).
export function EditUserDialog({
  open,
  user,
  onOpenChange,
  onSuccess,
}: EditUserDialogProps) {
  const { user: principal } = useAuth();
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [status, setStatus] = useState<UserStatus>("active");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    setName(user.name);
    setRole(user.role);
    setStatus(user.status);
    setError(null);
    setSubmitting(false);
  }, [open, user]);

  if (!user) return null;
  const isSelf = principal?.id === user.id;
  const trimmedName = name.trim();
  const dirty =
    trimmedName !== user.name ||
    (!isSelf && (role !== user.role || status !== user.status));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || !dirty) return;
    if (!trimmedName) {
      setError("Name cannot be empty.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const patch: { name?: string; role?: UserRole; status?: UserStatus } = {};
    if (trimmedName !== user.name) patch.name = trimmedName;
    if (!isSelf) {
      if (role !== user.role) patch.role = role;
      if (status !== user.status) patch.status = status;
    }
    try {
      const updated = await updateUser(user.id, patch);
      onSuccess(updated);
      onOpenChange(false);
    } catch (err) {
      setError(editErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !submitting && onOpenChange(next)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            Update the user&apos;s name, role, and status. Disabling kicks
            them out of all active sessions.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              value={user.email}
              readOnly
              disabled
              className="font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as UserRole)}
                disabled={isSelf || submitting}
              >
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {isSelf ? (
                <p className="text-xs text-muted-foreground">
                  You can&apos;t change your own role.
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as UserStatus)}
                disabled={isSelf || submitting}
              >
                <SelectTrigger id="edit-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
              {isSelf ? (
                <p className="text-xs text-muted-foreground">
                  You can&apos;t change your own status.
                </p>
              ) : null}
            </div>
          </div>

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !dirty}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
